import { isMobile, mobileConfig, webConfig } from './common';

export type CookieValue = string | number | boolean | null;

type Cookies = Record<string, CookieValue>;

type CookieOptions = {
    maxAge?: number | undefined;
    signed?: boolean | undefined;
    expires?: Date | undefined;
    httpOnly?: boolean | undefined;
    path?: string | undefined;
    domain?: string | undefined;
    secure?: boolean | undefined;
    sameSite?: boolean | 'lax' | 'strict' | 'none' | undefined;
};

const SAME_SITE_COOKIES = ['at', 'ixiUID', 'pwaSdkScope', 'sapphire', 'clientId'];

const parseCookies = () => {
    const config = isMobile() ? mobileConfig : webConfig;
    const cookies: Cookies = {};
    const parsedCookies =
        typeof window !== 'undefined'
            ? document.cookie.split(';').reduce((prevCookie, cookie) => {
                  if (!cookie) return prevCookie;
                  const [name, val] = cookie.trim().split('=');
                  // eslint-disable-next-line no-param-reassign
                  prevCookie[name] = decodeURIComponent(val.replace(/^"/, '').replace(/"$/, ''));
                  return prevCookie;
              }, cookies)
            : cookies;

    const { ixiUID: deviceid, clientId, apiKey, at, token } = parsedCookies;

    parsedCookies.apiKey = apiKey || config.apiKey;
    parsedCookies.ixiSrc = clientId || config.clientId;
    parsedCookies.clientId = clientId || config.clientId;

    if (deviceid) parsedCookies.deviceid = deviceid;
    if (at || token) parsedCookies.authorization = `Bearer ${at || token}`;

    return parsedCookies;
};

function writeCookie(key: string, value: number | string | boolean, { domain, expires }: CookieOptions = {}) {
    const cookieStr = `${key}=${value};${
        expires ? `expires=${expires.toUTCString()};` : ''
    }${'path=/;'}${domain ? `domain=${domain};` : ''}`;
    document.cookie = cookieStr;
}

export const getCookieHelpers = (): {
    getCookie(key: string): CookieValue;
    getAllCookies(): Cookies;
    setCookie(key: string, value: CookieValue, opts?: CookieOptions): void;
    clearCookie(key: string): void;
    syncCookies: () => void;
} => {
    let parsedCookies = parseCookies();

    return {
        getCookie: (key: string) => parsedCookies[key],
        getAllCookies: () => ({ ...parsedCookies }),
        setCookie: (key: string, val: string, opts?: object) => {
            writeCookie(key, val, { ...opts });
            parsedCookies[key] = val;
        },
        clearCookie: (key: string) => {
            let cookieStr = `${key}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;`;
            if (SAME_SITE_COOKIES.includes(key)) cookieStr += 'SameSite=None;Secure';
            document.cookie = cookieStr;

            cookieStr = `${key}=;domain=.ixigo.com;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/;`;
            if (SAME_SITE_COOKIES.includes(key)) cookieStr += 'SameSite=None;Secure';
            document.cookie = cookieStr;
        },
        syncCookies: () => {
            const cookies = parseCookies();
            parsedCookies = cookies;
        },
    };
};
